<template>
    <div>
        <!-- Program details (static) -->
        <div :class="containerClasses">
            <!-- Symbol or Logo -->
            <div class="lg:flex gap-6">
                <template v-if="brandingType === 'symbol' && symbol">
                    <div
                        class="relative h-20 w-20 lg:h-32 lg:w-32 flex-shrink-0 bg-white bg-cover bg-center"
                        :class="[layout === 'one' ? 'rounded' : '', corners]"
                        :style="{ backgroundImage: `url(${symbol})` }"
                    ></div>
                </template>

                <template v-else-if="brandingType === 'logo' && logo">
                    <div class="relative w-auto" style="max-width: 256px;">
                        <img :src="logo" class="w-auto h-auto" />
                    </div>
                </template>

                <!-- Title & Description -->
                <div class="flex-1 space-y-4 mt-4 lg:mt-0">
                    <div :class="textColorClass">
                        <div class="flex items-center justify-between gap-6">
                            <editable-text-field
                                text-type="h2"
                                v-model="portalContentAndDesignStore.leadsPageSettings.program_leads_page_title"
                                class="mb-1"
                            ></editable-text-field>

                            <editable-btn
                                size="small"
                                class="rounded-full hidden lg:flex"
                                :type="buttonColor"
                                v-model="portalContentAndDesignStore.translationSettings.portal_translation_program_page_visit_website"
                            >
                                <a class="whitespace-nowrap" :href="landingPageUrl" target="_blank">
                                    {{ portalContentAndDesignStore.translationSettings.portal_translation_program_page_visit_website }}
                                </a>
                                <icon class="ml-2" name="arrow-right-up" />
                            </editable-btn>
                        </div>
                        <editable-text-editor-field
                            v-if="portalContentAndDesignStore.leadsPageSettings.program_leads_page_description"
                            v-model="portalContentAndDesignStore.leadsPageSettings.program_leads_page_description"
                            :class="[
                                'partnero-page-content-section text-md text-opacity-80',
                                textColorClass
                            ]"
                        />

                        <editable-btn
                            size="small"
                            class="mt-4 flex lg:hidden"
                            :type="buttonColor"
                            v-model="portalContentAndDesignStore.translationSettings.portal_translation_program_page_visit_website"
                        >
                          <span>
                              <a class="whitespace-nowrap" :href="landingPageUrl" target="_blank">
                                  {{ portalContentAndDesignStore.translationSettings.portal_translation_program_page_visit_website }}
                              </a>
                          </span>
                            <icon class="ml-2" name="arrow-right-up"></icon>
                        </editable-btn>
                    </div>

                    <!-- Log in / Sign up links -->
                    <div v-if="portalContentAndDesignStore.leadsPageSettings.program_leads_include_signup_links" class="flex items-center gap-2">
                        <editable-btn
                            size="small"
                            :type="buttonColor"
                            :href="loginLink"
                            v-model="portalContentAndDesignStore.translationSettings.portal_translation_program_page_log_in"
                        />
                        <editable-text-field
                            v-if="!disableRegistration"
                            class="text-sm"
                            :class="textColorClass"
                            v-model="portalContentAndDesignStore.translationSettings.portal_translation_program_page_or"
                            text-type="span"
                        >
                        </editable-text-field>

                        <editable-btn
                            v-if="!disableRegistration"
                            size="small"
                            :type="buttonColor"
                            :href="signupLink"
                            v-model="portalContentAndDesignStore.translationSettings.portal_translation_program_page_sign_up"
                        />
                    </div>
                </div>
            </div>

            <!-- Additional section -->
            <div
                v-if="portalContentAndDesignStore.leadsPageSettings.program_leads_include_additional_section
                  && portalContentAndDesignStore.leadsPageSettings.program_leads_page_additional_description"
                :class="[
                'bg-black bg-opacity-5 p-6 mt-6',
                layout === 'one' ? 'rounded' : '', corners
            ]"
            >
                <editable-text-editor-field
                    :class="[
                    'partnero-page-content-section text-md text-opacity-80',
                    textColorClass
                ]"
                    v-model="portalContentAndDesignStore.leadsPageSettings.program_leads_page_additional_description"
                />
            </div>
        </div>
        <!-- Lead form -->
        <div :class="formWrapperClasses">

          <form-errors :store-form-errors="portalContentAndDesignStore.leadsFormErrors"/>
          <alert v-if="leadSubmissionSuccessMessage"
                 type="success"
          >
            {{ leadSubmissionSuccessMessage }}
          </alert>

            <form
                id="program-lead"
            >
                <input type="hidden" name="_token" :value="csrfToken" />

                <div class="mb-8">
                    <program-lead-logged-partner
                        :logged-partner="portalContentAndDesignStore.leadsPageSettings.logged_partner"
                        :login-config="portalContentAndDesignStore.leadsPageSettings.login_config"
                        :translations=" portalContentAndDesignStore.translationSettings"
                    />
                </div>

                <editable-text-field
                    text-type="custom"
                    :class="['text-lg md:text-2xl font-semibold mb-4', portalContentAndDesignStore.getPageContentTextColorClass]"
                    v-model="portalContentAndDesignStore.translationSettings.portal_translation_leads_lead_details"
                />

                <leads-custom-fields />

                <div class="mt-8">
                    <editable-btn
                        :type="buttonColor"
                        v-model="portalContentAndDesignStore.translationSettings.portal_translation_leads_submit_a_lead"
                        :disabled="submittingLeadForm"
                        :loading="submittingLeadForm"
                        @click="submitLead"
                    />
                </div>
            </form>
        </div>
    </div>
</template>

<script setup>
import {computed, ref} from 'vue'

import {usePortalContentAndDesignStore} from "../../../../../stores/partners/portalContentAndDesignStore";
import EditableTextField from "../../../portal-editable-fields/EditableTextField.vue";
import EditableBtn from "../../../portal-editable-fields/EditableBtn.vue";
import EditableTextEditorField
    from "../../../portal-editable-fields/EditableTextEditorField.vue";
import LeadsCustomFields from "./includes/LeadsCustomFields.vue";
import Alert from "../../../../../elements/Alert.vue";
import FormErrors from "../Errors/FormErrors.vue";

const portalContentAndDesignStore = usePortalContentAndDesignStore()

const props = defineProps({
    csrfToken: {
        type: String,
        required: true
    }
})

const getFormActionRoute = route('partners_int.leads.submit')

const leadSubmissionSuccessMessage = ref(null)
const submittingLeadForm = ref(false)

const layout = computed(() => portalContentAndDesignStore.portalLayoutSettings.public_page_content_layout)
const corners = computed(() => portalContentAndDesignStore.portalLayoutSettings.public_page_content_layout_corners !== 'none'
    ? portalContentAndDesignStore.portalLayoutSettings.public_page_content_layout_corners
    : '')

const textColorClass = computed(() => portalContentAndDesignStore.getPageContentTextColorClass)
const buttonColor = computed(() => portalContentAndDesignStore.getPageContentButtonColorClass)

const disableRegistration = computed(() => portalContentAndDesignStore.portalLayoutSettings.referral_portal_portal_disable_registrations_via_portal || false);

const symbol = computed(() => portalContentAndDesignStore.srcRoutes.portal_layout_file_symbol)
const logo = computed(() => portalContentAndDesignStore.srcRoutes.portal_layout_file_logo)
const brandingType = computed(() => portalContentAndDesignStore.leadsPageSettings.program_leads_page_branding_type)

const loginLink = computed(() => portalContentAndDesignStore.getLoginRoute)
const signupLink = computed(() => portalContentAndDesignStore.getRegisterRoute)
const landingPageUrl = computed(() => portalContentAndDesignStore.getLandingPageSettings.landing_page_url || '/')

const containerClasses = computed(() => {
    // Add some conditions here if needed based on content layout
    return 'space-y-6 pb-8 lg:pb-12 pt-8 lg:pt-20 px-8 lg:px-14'
})

const formWrapperClasses = computed(() => {
    return [
        'pt-8 lg:pt-12 pb-8 lg:pb-20 space-y-12 border-t px-8 lg:px-14',
        textColorClass.value
    ]
})

const submitLead = () => {

    leadSubmissionSuccessMessage.value = null
    submittingLeadForm.value = true

    portalContentAndDesignStore.resetFormErrors('leads')

    axios.post(getFormActionRoute, portalContentAndDesignStore.leadsFormData)
        .then(response => {
            if (+response.data.status === 1) {

                leadSubmissionSuccessMessage.value = response.data.message

                portalContentAndDesignStore.resetLeadsFormAndErrorData()
            } else {
                portalContentAndDesignStore.leadsFormErrors.responseError = response.data?.error;
            }
        })
        .catch(error => {
            portalContentAndDesignStore.leadsFormErrors.validationErrors = error.response?.data?.errors;
        })
        .finally(() => {
            submittingLeadForm.value = false;
        });
}
</script>
